/**
 * ConnectSetupHost — mounts the "one more detail for <app>" setup form over
 * ANY page (2026-08-24 WhatsApp repro). When an in-chat connect card click
 * hits the needs-setup case (the app requires initiation values — the
 * 2026-08-14 WABA class — before its sign-in can start), chat used to post a
 * message pointing at the Integrations page and stop: the popup flashed
 * shut, nothing visibly happened, and no connection was ever created.
 *
 * Now the chat lane dispatches `prest0n:connect-setup` and this host opens
 * the SAME SetupFieldsDialog the Integrations page uses, right over the
 * conversation. Submit re-enters the connect flow with the collected
 * initFields — the submit click is a fresh user gesture, so the OAuth
 * window opens uninhibited. When the event carries a `resume` (the chat
 * lane's own continuation), submit runs THROUGH it so success narrates
 * composio.connection_complete in the thread exactly like a direct card
 * click; the direct engine call remains the fallback.
 *
 * Hard-won invariants (2026-08-25 adversarial review):
 *  - The dialog opens IN FRONT of the chat: the event fires from inside the
 *    chat, and a small modal z-index buried the dialog invisibly behind the
 *    surface that opened it. This host used to carry its own override; since
 *    2026-09-11 PanelModal's own default is the correct rung, so there is no
 *    bespoke number here to drift out of step with the ladder.
 *  - Every async continuation is generation-guarded and cancel aborts the
 *    in-flight flow: without both, a dismissed dialog resurrected itself up
 *    to two minutes later when the abandoned poll finally resolved, and a
 *    stale attempt could stomp a newer dialog's typed values.
 *
 * detail: { toolkitSlug: string; displayName?: string; resume?: ConnectSetupResume }
 */

import React, { useEffect, useRef, useState } from 'react';
import SetupFieldsDialog, { type SetupPromptState } from '../services/composio/panel/SetupFieldsDialog';
import {
  getConnectRequiredFields,
  connectAppWithKey,
  type ConnectInitField,
} from '../services/composio/ComposioClient';
import { runConnectPopupFlow } from '../services/composio/connectPopupFlow';
import type { ConnectSetupResume } from '../services/globalTools/tools/connectApp';

export const CONNECT_SETUP_EVENT = 'prest0n:connect-setup';

/**
 * KEY-LANE COPY (2026-09-03). Code may author FAILURE / REFUSAL copy (D9), and
 * these three lines are the whole user-facing contract of the key lane.
 *
 * "connected" is said for ONE outcome only. Composio answers ACTIVE for a key
 * it has never checked, so the server makes a real call before it will claim
 * the key works — and an `unverified` answer still carries a connected account
 * id. Reading that id as success is precisely the mistake the server's
 * `connected` flag exists to prevent, so this dialog reads the flag and
 * nothing else.
 */
export const KEY_LANE_COPY = {
  rejected: {
    en: "that key wasn't accepted — check you copied all of it, then try again",
    es: 'esa clave no fue aceptada: revisa que la copiaste completa e inténtalo de nuevo',
  },
  unverified: {
    en: "saved — Prest0n couldn't check this key just now, and will tell you the first time it uses it",
    es: 'guardada: Prest0n no pudo verificar esta clave ahora y te avisará la primera vez que la use',
  },
  failed: {
    en: 'saving that key did not work — try again in a moment',
    es: 'no se pudo guardar esa clave: inténtalo de nuevo en un momento',
  },
} as const;

export type KeyLaneLang = 'en' | 'es';
/** Fired when a dialog-driven connect completes — chat flips the standing
 *  connect card to its done state so it stops inviting a second OAuth. */
export const CONNECT_SETUP_CONNECTED_EVENT = 'prest0n:connect-setup-connected';

export const ConnectSetupHost: React.FC = () => {
  const [prompt, setPrompt] = useState<SetupPromptState | null>(null);
  const [displayName, setDisplayName] = useState<string>('');
  // Generation counter: bumped on every open and every cancel. Async
  // continuations capture the generation at start and drop their state
  // writes when it moved — the no-resurrect / no-stomp guard.
  const genRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const resumeRef = useRef<ConnectSetupResume | null>(null);
  const modeRef = useRef<'oauth' | 'key'>('oauth');
  const langRef = useRef<KeyLaneLang>('en');

  useEffect(() => {
    const onEvent = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | {
            toolkitSlug?: string;
            displayName?: string;
            resume?: ConnectSetupResume;
            /** 'key' collects a pasted credential instead of starting OAuth. */
            mode?: 'oauth' | 'key';
            lang?: KeyLaneLang;
          }
        | undefined;
      const slug = String(detail?.toolkitSlug || '').trim().toLowerCase();
      if (!/^[a-z0-9_-]{1,64}$/.test(slug)) return;
      // A new open supersedes anything in flight (a second card click while
      // an old attempt still polls must not let the old attempt write back).
      genRef.current += 1;
      const gen = genRef.current;
      abortRef.current?.abort();
      abortRef.current = null;
      resumeRef.current = typeof detail?.resume === 'function' ? detail.resume : null;
      // Both are read at SUBMIT time, so they must be set on every open —
      // otherwise a key dialog opened after an OAuth one would submit down the
      // OAuth path, and vice versa.
      modeRef.current = detail?.mode === 'key' ? 'key' : 'oauth';
      langRef.current = detail?.lang === 'es' ? 'es' : 'en';
      setDisplayName(String(detail?.displayName || slug).slice(0, 80));
      // Catalog metadata drives the form — zero per-app code (the same
      // pre-check the Integrations page runs before its own connects).
      // The KEY lane asks a different question of the same slug — what the
      // paste-a-key mint needs, not what the OAuth sign-in needs. The OAuth
      // answer is deliberately EMPTY for these apps (that emptiness is what
      // routes them to the browser lane), so asking it here would render a
      // dialog with no fields at all.
      void getConnectRequiredFields(slug, { mode: modeRef.current })
        .then((meta) => {
          if (gen !== genRef.current) return;
          if (meta.required.length === 0) return; // nothing to collect
          setPrompt({
            slug,
            fields: meta.required,
            values: Object.fromEntries(meta.required.map((f: ConnectInitField) => [f.name, ''])),
            ...(meta.help ? { help: meta.help } : {}),
          });
        })
        .catch(() => {
          // Metadata fetch failed — the standing finish-setup pill on the
          // chat card remains the path; never a broken empty dialog.
        });
    };
    document.addEventListener(CONNECT_SETUP_EVENT, onEvent as EventListener);
    return () => document.removeEventListener(CONNECT_SETUP_EVENT, onEvent as EventListener);
  }, []);

  if (!prompt) return null;

  const close = () => {
    // Cancel is final for this attempt: kill the in-flight poll so it can
    // neither resurrect the dialog later nor complete a connect the user
    // walked away from.
    genRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    setPrompt(null);
  };

  const submit = async () => {
    const missing = prompt.fields.filter((f) => !(prompt.values[f.name] || '').trim());
    if (missing.length > 0) {
      setPrompt({ ...prompt, note: `Please fill in: ${missing.map((f) => f.displayName).join(', ')}` });
      return;
    }
    const { slug, values } = prompt;
    const gen = genRef.current;
    setPrompt({ ...prompt, submitting: true, note: undefined });
    // Popup-block is a CALLBACK, not an outcome: the engine keeps polling
    // behind a fallback link. Here the honest move is to stop this attempt
    // (abort → 'aborted') and hand the user a fresh submit — a new click is
    // a new user gesture, so the retry opens uninhibited.
    // KEY LANE: no popup, no redirect — the server mints and then PROVES the
    // key with a real read-only call, and answers with a verdict.
    if (modeRef.current === 'key') {
      const lang = langRef.current;
      let result: Awaited<ReturnType<typeof connectAppWithKey>> | null = null;
      try {
        result = await connectAppWithKey(slug, values);
      } catch {
        result = null;
      }
      if (gen !== genRef.current) return; // cancelled or superseded meanwhile
      // The FLAG, never the account id: an unverified answer carries one.
      if (result?.connected) {
        try {
          document.dispatchEvent(
            new CustomEvent(CONNECT_SETUP_CONNECTED_EVENT, { detail: { toolkitSlug: slug } })
          );
        } catch { /* non-DOM environments */ }
        setPrompt(null);
        return;
      }
      setPrompt({
        ...prompt,
        submitting: false,
        note: result?.verdict === 'rejected'
          ? KEY_LANE_COPY.rejected[lang]
          : result?.verdict === 'unverified'
            ? KEY_LANE_COPY.unverified[lang]
            : KEY_LANE_COPY.failed[lang],
      });
      return;
    }

    let popupBlocked = false;
    const abort = new AbortController();
    abortRef.current = abort;
    const onBlocked = () => {
      popupBlocked = true;
      abort.abort();
    };
    // The submit click is the user gesture — the OAuth popup opens inside
    // it (the resume call and the direct engine call are both synchronous
    // up to window.open).
    const resume = resumeRef.current;
    const outcome = resume
      ? await resume(values, abort.signal, onBlocked)
      : await runConnectPopupFlow({
          toolkitSlug: slug,
          displayName: displayName || slug,
          initFields: values,
          surface: 'chat-setup',
          signal: abort.signal,
          callbacks: { onPopupBlocked: onBlocked },
        });
    if (gen !== genRef.current) return; // cancelled or superseded meanwhile
    abortRef.current = null;
    if (outcome === 'connected') {
      // Tell chat so the standing connect card flips to done instead of
      // inviting a redundant second OAuth (resume already narrated the
      // completion in the thread).
      try {
        document.dispatchEvent(
          new CustomEvent(CONNECT_SETUP_CONNECTED_EVENT, { detail: { toolkitSlug: slug } })
        );
      } catch { /* non-DOM environments */ }
      setPrompt(null);
      return;
    }
    if (outcome === 'needs-setup') {
      // Still missing something — refresh the field list and keep the form.
      const meta = await getConnectRequiredFields(slug, { mode: modeRef.current }).catch(() => null);
      if (gen !== genRef.current) return;
      setPrompt({
        slug,
        fields: meta?.required?.length ? meta.required : prompt.fields,
        values,
        note: 'one of the values was not accepted — double-check and try again',
        ...(meta?.help ? { help: meta.help } : {}),
      });
      return;
    }
    if (outcome === 'unsupported') {
      // No one-click sign-in for this app (2026-09-02). The engine has
      // already opened Prest0n's browser sign-in behind this dialog — leaving
      // a "could not start" note over a lane that IS starting would be the
      // dialog contradicting the surface. Close and let that lane speak.
      setPrompt(null);
      return;
    }
    setPrompt({
      ...prompt,
      submitting: false,
      note: popupBlocked
        ? 'the sign-in window was blocked — allow pop-ups for this site and submit again'
        : outcome === 'denied'
          ? 'connecting apps needs a permission this account does not have — ask an owner or admin to connect it'
          : outcome === 'timeout'
            ? "the sign-in window timed out — submit again when you're ready"
            : 'the connection could not start — try again, or connect from the Integrations page',
    });
  };

  return (
    <SetupFieldsDialog
      prompt={prompt}
      onChange={setPrompt}
      onSubmit={() => { void submit(); }}
      onCancel={close}
    />
  );
};

export default ConnectSetupHost;
